import React, { useState } from 'react';
import { X, ChevronLeft, MapPin, Star, Users, ExternalLink } from 'lucide-react';
import { stockPhoto } from '../../assets/stock/index.ts';

interface MockHomeCareProvider {
    id: number;
    name: string;
    suburb: string;
    state: string;
    distance: number;
    rating: number;
    packageLevels: string[];
    services: string[];
    coverage: string;
    websiteUrl: string;
    images: string[];
}

export interface HomeCareProviderSearchProps {
    onClose: () => void;
}

const HomeCareProviderSearch: React.FC<HomeCareProviderSearchProps> = ({ onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [maxDistance, setMaxDistance] = useState(10);
    const [packageLevel, setPackageLevel] = useState('all');
    const [showResults, setShowResults] = useState(false);

    // Mock home care provider data
    const mockProviders: MockHomeCareProvider[] = [
        {
            id: 1,
            name: "Caring Hands Home Care",
            suburb: "Croydon",
            state: "NSW",
            distance: 1.2,
            rating: 4.3,
            packageLevels: ["Level 1 ($10,698)", "Level 2 ($15,982)", "Level 3 ($21,920)", "Level 4 ($29,545)", "Level 5 ($39,535)", "Level 6 ($47,957)", "Level 7 ($58,122)", "Level 8 ($77,709)"],
            services: ["Personal Care", "Nursing", "Allied Health", "Social Support"],
            coverage: "Inner West Sydney",
            websiteUrl: 'https://example.org/carecompanion-demo/providers/caring-hands-home-care',
            images: [
                stockPhoto('1559027615-cd4628902d4a', 600, 400),
                stockPhoto('1586023492125-27b2c045efd7', 600, 400),
                stockPhoto('1560448204-e02f11c3d0e2', 600, 400)
            ]
        },
        {
            id: 2,
            name: "Comfort Care Services",
            suburb: "Five Dock",
            state: "NSW",
            distance: 2.1,
            rating: 4.6,
            packageLevels: ["Level 2 ($15,982)", "Level 3 ($21,920)", "Level 4 ($29,545)", "Level 5 ($39,535)", "Level 6 ($47,957)"],
            services: ["Personal Care", "Nursing", "Allied Health", "Social Support", "Respite Care"],
            coverage: "Inner West Sydney",
            websiteUrl: 'https://example.org/carecompanion-demo/providers/comfort-care-services',
            images: [
                stockPhoto('1571896349842-33c89424de2d', 600, 400),
                stockPhoto('1564013799919-ab600027ffc6', 600, 400),
                stockPhoto('1512917774080-9991f1c4c750', 600, 400)
            ]
        },
        {
            id: 3,
            name: "Gentle Touch Home Care",
            suburb: "Campsie",
            state: "NSW",
            distance: 3.4,
            rating: 4.1,
            packageLevels: ["Level 1 ($10,698)", "Level 2 ($15,982)", "Level 3 ($21,920)", "Level 4 ($29,545)"],
            services: ["Personal Care", "Nursing", "Social Support"],
            coverage: "Canterbury-Bankstown",
            websiteUrl: 'https://example.org/carecompanion-demo/providers/gentle-touch-home-care',
            images: [
                stockPhoto('1560448204-603b3fc33ddc', 600, 400),
                stockPhoto('1582407947304-fd86f028f716', 600, 400),
                stockPhoto('1559027615-cd4628902d4a', 600, 400)
            ]
        },
        {
            id: 4,
            name: "Sunrise Home Care",
            suburb: "Burwood",
            state: "NSW",
            distance: 4.7,
            rating: 4.4,
            packageLevels: ["Level 3 ($21,920)", "Level 4 ($29,545)", "Level 5 ($39,535)", "Level 6 ($47,957)", "Level 7 ($58,122)"],
            services: ["Personal Care", "Nursing", "Allied Health", "Social Support", "Respite Care"],
            coverage: "Inner West Sydney",
            websiteUrl: 'https://example.org/carecompanion-demo/providers/sunrise-home-care',
            images: [
                stockPhoto('1586023492125-27b2c045efd7', 600, 400),
                stockPhoto('1571896349842-33c89424de2d', 600, 400),
                stockPhoto('1564013799919-ab600027ffc6', 600, 400)
            ]
        },
        {
            id: 5,
            name: "Family First Care",
            suburb: "Ashfield",
            state: "NSW",
            distance: 2.8,
            rating: 4.0,
            packageLevels: ["Level 1 ($10,698)", "Level 2 ($15,982)", "Level 3 ($21,920)", "Level 4 ($29,545)", "Level 5 ($39,535)", "Level 6 ($47,957)", "Level 7 ($58,122)", "Level 8 ($77,709)"],
            services: ["Personal Care", "Nursing", "Allied Health", "Social Support", "Respite Care"],
            coverage: "Inner West Sydney",
            websiteUrl: 'https://example.org/carecompanion-demo/providers/family-first-care',
            images: [
                stockPhoto('1512917774080-9991f1c4c750', 600, 400),
                stockPhoto('1560448204-e02f11c3d0e2', 600, 400),
                stockPhoto('1582407947304-fd86f028f716', 600, 400)
            ]
        }
    ];

    const filteredProviders = mockProviders
        .filter(provider => {
            const distanceMatch = provider.distance <= maxDistance;
            const packageMatch = packageLevel === 'all' || provider.packageLevels.includes(packageLevel);
            return distanceMatch && packageMatch;
        })
        .sort((a, b) => a.distance - b.distance);

    const handleSearch = () => {
        if (searchTerm.trim()) {
            setShowResults(true);
        }
    };

    const handleBackToSearch = () => {
        setShowResults(false);
        setSearchTerm('');
    };

    const handleACATApplication = () => {
        window.open('https://www.myagedcare.gov.au/assessment/apply-online', '_blank');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {showResults ? `Home Care Providers near ${searchTerm}` : 'Find Home Care Providers'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>
                
                <div className="p-6">
                    {!showResults ? (
                        // Search Interface
                        <div className="space-y-6">
                            <div className="text-center">
                                <p className="text-gray-600 mb-6">Enter your suburb or area to find nearby home care service providers</p>
                            </div>
                            
                            <div className="max-w-md mx-auto space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Suburb or Area</label>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        placeholder="e.g., Croydon, Five Dock, Campsie..."
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Max Distance: {maxDistance}km</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="20"
                                        value={maxDistance}
                                        onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                                        className="w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Package Level</label>
                                    <select
                                        value={packageLevel}
                                        onChange={(e) => setPackageLevel(e.target.value)}
                                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="all">All Package Levels</option>
                                        <option value="Level 1">Level 1 ($10,698)</option>
                                        <option value="Level 2">Level 2 ($15,982)</option>
                                        <option value="Level 3">Level 3 ($21,920)</option>
                                        <option value="Level 4">Level 4 ($29,545)</option>
                                        <option value="Level 5">Level 5 ($39,535)</option>
                                        <option value="Level 6">Level 6 ($47,957)</option>
                                        <option value="Level 7">Level 7 ($58,122)</option>
                                        <option value="Level 8">Level 8 ($77,709)</option>
                                    </select>
                                </div>
                                
                                <button
                                    onClick={handleSearch}
                                    disabled={!searchTerm.trim()}
                                    className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-lg font-semibold"
                                >
                                    Search Providers
                                </button>
                            </div>

                            {/* ACAT Application Section */}
                            <div className="mt-8 p-6 bg-white rounded-lg border border-blue-200">
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                                            <ExternalLink className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-blue-900 mb-2">Need an aged care needs assessment for Support at Home?</h3>
                                        <p className="text-blue-800 mb-4">
                                            To access Support at Home, you need an aged care needs assessment. 
                                            This determines your care needs and Support at Home classification.
                                        </p>
                                        <button
                                            onClick={handleACATApplication}
                                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center gap-2"
                                        >
                                            <ExternalLink size={16} />
                                            Apply via My Aged Care
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Results Interface
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <button
                                    onClick={handleBackToSearch}
                                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    <ChevronLeft size={20} />
                                    Back to Search
                                </button>
                            </div>

                            {/* Results */}
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                                {filteredProviders.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No providers found matching your criteria.
                                    </div>
                                ) : (
                                    filteredProviders.map(provider => (
                                        <div
                                            key={provider.id}
                                            className="border border-gray-200 rounded-xl p-4 md:p-5 hover:border-blue-300 hover:shadow-md transition-all"
                                        >
                                            <div className="flex flex-col md:flex-row gap-5">
                                                <div className="shrink-0 w-full md:w-[min(100%,280px)] md:max-w-[280px]">
                                                    <div className="aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 ring-1 ring-slate-200/80">
                                                        {provider.images?.[0] ? (
                                                            <img
                                                                src={provider.images[0]}
                                                                alt={provider.name}
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">No photo</div>
                                                        )}
                                                    </div>
                                                    {provider.images && provider.images.length > 1 ? (
                                                        <div className="flex gap-1.5 mt-2">
                                                            {provider.images.slice(1, 4).map((image, index) => (
                                                                <img
                                                                    key={index}
                                                                    src={image}
                                                                    alt={`${provider.name} view ${index + 2}`}
                                                                    className="h-14 min-w-0 flex-1 object-cover rounded-lg border border-slate-200"
                                                                />
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="flex-1 min-w-0 flex flex-col lg:flex-row gap-4 lg:gap-6">
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-xl font-bold text-gray-900 leading-tight">{provider.name}</h3>
                                                        <p className="text-gray-600 mt-0.5">{provider.suburb}, {provider.state}</p>
                                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-slate-600">
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <MapPin size={15} className="text-sky-600 shrink-0" aria-hidden />
                                                                {provider.distance}km away
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <Star size={15} className="text-amber-500 shrink-0" aria-hidden />
                                                                {provider.rating}/5
                                                            </span>
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <Users size={15} className="text-violet-600 shrink-0" aria-hidden />
                                                                {provider.coverage}
                                                            </span>
                                                        </div>
                                                        <div className="mt-3">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Package levels</span>
                                                            <p className="text-sm text-gray-800 mt-0.5 leading-snug">{provider.packageLevels.join(', ')}</p>
                                                        </div>
                                                        <div className="mt-2">
                                                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Services</span>
                                                            <p className="text-sm text-gray-800 mt-0.5">{provider.services.join(', ')}</p>
                                                        </div>
                                                        {provider.websiteUrl ? (
                                                            <a
                                                                href={provider.websiteUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-800"
                                                            >
                                                                <ExternalLink size={14} aria-hidden />
                                                                Visit website
                                                                <span className="text-xs font-normal text-gray-500">(demo URL)</span>
                                                            </a>
                                                        ) : null}
                                                    </div>
                                                    <div className="shrink-0 flex lg:flex-col justify-end lg:justify-center gap-2 lg:w-44">
                                                        <button
                                                            type="button"
                                                            onClick={() => window.open('https://www.myagedcare.gov.au/find-a-provider', '_blank')}
                                                            className="w-full lg:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-semibold shadow-sm"
                                                        >
                                                            Contact Provider
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HomeCareProviderSearch;
